// MEMO: MoneyAmount 等の enum を実行時 import すると api/generated/api.ts 経由で axios(ESM) を
// 取り込み jest が解釈できなくなるため、型のみインポートし、比較には文字列定数を使う
// （mypage/startupProfileModalLogic.ts と同じ回避策）。
import type { User } from "@/api/generated";

const UNSET_LABEL = "未設定";
const FINANCIAL_ASSET_UNSELECTED = "未選択";

/**
 * 希望物件種別の表示ラベルを生成する。
 *
 * @param user - 対象ユーザー（null / undefined 可）
 * @returns 希望物件種別名を「／」区切りで連結した文字列。未入力・空配列のときは「未設定」
 */
export const buildPropertyTypesLabel = (user: User | null | undefined): string => {
  const names = (user?.master_interested_property_types ?? [])
    .map((master) => master.name)
    .filter((name): name is string => !!name);
  return names.length > 0 ? names.join("／") : UNSET_LABEL;
};

/**
 * エリア（都道府県）の表示ラベルを生成する。
 *
 * @param user - 対象ユーザー（null / undefined 可）
 * @returns 都道府県名。未入力のときは「未設定」
 */
export const buildAreaLabel = (user: User | null | undefined): string => user?.address_prefecture || UNSET_LABEL;

/**
 * 年収の表示ラベルを生成する。
 *
 * @param user - 対象ユーザー（null / undefined 可）
 * @returns 年収に「万円」を付けた文字列（例: 800万円）。未入力のときは「未設定」
 */
export const buildAnnualIncomeLabel = (user: User | null | undefined): string =>
  typeof user?.annual_income === "number" ? `${user.annual_income}万円` : UNSET_LABEL;

/**
 * 金融資産の表示ラベルを生成する。
 *
 * @param user - 対象ユーザー（null / undefined 可）
 * @returns 資産帯の文字列（例: 500〜1,000万円）。未入力・「未選択」のときは「未設定」
 */
export const buildFinancialAssetLabel = (user: User | null | undefined): string =>
  user?.financial_asset && (user.financial_asset as string) !== FINANCIAL_ASSET_UNSELECTED
    ? user.financial_asset
    : UNSET_LABEL;

/**
 * 投資ステージの表示ラベルを生成する。
 *
 * @param user - 対象ユーザー（null / undefined 可）
 * @returns 投資経験ありなら「経験あり」、なしなら「これから1件目」。未入力のときは「未設定」
 */
export const buildStageLabel = (user: User | null | undefined): string => {
  if (typeof user?.has_investment_experience !== "boolean") return UNSET_LABEL;
  return user.has_investment_experience ? "経験あり" : "これから1件目";
};
